import React, { useState, useRef, useEffect, ReactNode } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrop, faSyncAlt, faArrowsAltH, faCheck, faArrowsAltV, faPlus, faMinus, faUndo, faExpand, IconDefinition, faCompress, faMagnifyingGlassPlus, faMagnifyingGlassMinus } from '@fortawesome/free-solid-svg-icons';
import { IMediaItem } from '../../../models/Interfaces';
import { Button } from 'react-bootstrap';

interface ICropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

type CropHandle = "move" | "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const minCropSize = 8;

const clampCrop = (rect: ICropRect, maxWidth: number, maxHeight: number): ICropRect => {
  let width = Math.max(minCropSize, rect.width);
  let height = Math.max(minCropSize, rect.height);
  width = Math.min(width, maxWidth);
  height = Math.min(height, maxHeight);
  const x = Math.max(0, Math.min(rect.x, maxWidth - width));
  const y = Math.max(0, Math.min(rect.y, maxHeight - height));
  return { x, y, width, height };
};

const cursorForHandle = (handle: CropHandle | "new"): string => {
  switch (handle) {
    case "n":
    case "s":
      return "ns-resize";
    case "e":
    case "w":
      return "ew-resize";
    case "ne":
    case "sw":
      return "nesw-resize";
    case "nw":
    case "se":
      return "nwse-resize";
    case "new":
      return "crosshair";
    default:
      return "move";
  }
};

const cropHandleNames = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;

const cropHandleStyle = (handle: typeof cropHandleNames[number]): React.CSSProperties => {
  const horizontal = handle.includes("w") ? "0%" : handle.includes("e") ? "100%" : "50%";
  const vertical = handle.includes("n") ? "0%" : handle.includes("s") ? "100%" : "50%";
  return {
    position: "absolute",
    width: 10,
    height: 10,
    left: horizontal,
    top: vertical,
    marginLeft: -5,
    marginTop: -5,
    background: "#fff",
    border: "1px solid #16324f",
    boxSizing: "border-box",
    pointerEvents: "none"
  };
};

interface IImageEditorProps {
  mediaItem: IMediaItem;
  mimeType: string;
  onApplyChanges: (updatedImageBlob: Blob) => void;
  onSave?: (updatedImageBlob: Blob) => Promise<boolean> | boolean;
  saveHint?: string;
  onFullScreenToggle: (fullscreen: boolean) => void;
}

const ImageEditor: React.FC<IImageEditorProps> = ({ mediaItem, mimeType, onApplyChanges, onSave, saveHint, onFullScreenToggle }) => {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [fileSize, setFileSize] = useState<string>("");
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [zoom, setZoom] = useState(1);
  const [editVersion, setEditVersion] = useState(0);
  const [appliedVersion, setAppliedVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cropping, setCropping] = useState(false);
  const [cropRect, setCropRect] = useState<ICropRect | null>(null);
  const [pixelEdits, setPixelEdits] = useState(false);
  const [cropCursor, setCropCursor] = useState("crosshair");
  const [canvasBox, setCanvasBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const croppingRef = useRef(false);
  const pixelEditsRef = useRef(false);
  const cropRectRef = useRef<ICropRect | null>(null);
  const cropCommitLock = useRef(false);
  const cropDragRef = useRef(false);
  const cropDragEndRef = useRef<(() => void) | null>(null);
  const loadGenerationRef = useRef(0);
  const editVersionRef = useRef(0);
  const editSnapshotRef = useRef({ pixelEdits: false, transformed: false });
  const commitCropRef = useRef<() => boolean>(() => false);
  const initialImageRef = useRef<HTMLImageElement | null>(null);
  const srcCanvasRef = useRef<HTMLCanvasElement>(null);
  const destCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewFrameRef = useRef<HTMLDivElement>(null);
  const sourceReadyRef = useRef(false);
  const [ready, setReady] = useState(false);
  const fullscreenicon: IconDefinition = faExpand;
  const normalviewicon: IconDefinition = faCompress;
  const showCanvas = ready && (cropping || pixelEdits || rotation !== 0 || scale !== 1 || zoom !== 1 || flipHorizontal || flipVertical);
  croppingRef.current = cropping;
  pixelEditsRef.current = pixelEdits;
  cropRectRef.current = cropRect;
  editSnapshotRef.current = {
    pixelEdits,
    transformed: rotation !== 0 || scale !== 1 || zoom !== 1 || flipHorizontal || flipVertical
  };

  const fitPreview = () => {
    const canvas = destCanvasRef.current;
    const frame = previewFrameRef.current;
    if (!canvas || !frame || canvas.width === 0 || canvas.height === 0) {
      return;
    }

    const styles = window.getComputedStyle(frame);
    const padX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    const padY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
    const availableWidth = frame.clientWidth - padX;
    const availableHeight = frame.clientHeight - padY;
    if (availableWidth <= 0 || availableHeight <= 0) {
      return;
    }

    const scaleToFit = Math.min(availableWidth / canvas.width, availableHeight / canvas.height);
    canvas.style.boxSizing = "border-box";
    canvas.style.width = `${Math.max(1, Math.floor(canvas.width * scaleToFit))}px`;
    canvas.style.height = `${Math.max(1, Math.floor(canvas.height * scaleToFit))}px`;
  };

  // Paint the picture straight onto the canvas. The file address is enough for this.
  // A resize helper that reads the pixels can fail and leave the canvas blank.
  const loadImage = (preserveCrop = false) => {
    const generation = ++loadGenerationRef.current;
    sourceReadyRef.current = false;
    setReady(false);
    if (!preserveCrop) {
      cropCommitLock.current = false;
      croppingRef.current = false;
      pixelEditsRef.current = false;
      cropRectRef.current = null;
      editVersionRef.current = 0;
      setEditVersion(0);
      setAppliedVersion(0);
      setSaveError(null);
      setCropping(false);
      setCropRect(null);
      setPixelEdits(false);
    }

    const img = new Image();
    img.src = mediaItem.Url;
    initialImageRef.current = img;

    img.onload = () => {
      if (generation !== loadGenerationRef.current) {
        return;
      }

      const srcCanvas = srcCanvasRef.current;
      const destCanvas = destCanvasRef.current;
      if (!srcCanvas || !destCanvas) {
        return;
      }

      const srcCtx = srcCanvas.getContext("2d");
      srcCanvas.width = img.naturalWidth || img.width;
      srcCanvas.height = img.naturalHeight || img.height;
      srcCtx?.drawImage(img, 0, 0);

      const destCtx = destCanvas.getContext("2d");
      destCanvas.width = srcCanvas.width;
      destCanvas.height = srcCanvas.height;
      destCtx?.drawImage(img, 0, 0);

      const keptRect = preserveCrop && croppingRef.current ? cropRectRef.current : null;
      if (keptRect) {
        const next = clampCrop(keptRect, destCanvas.width, destCanvas.height);
        cropRectRef.current = next;
        setCropRect(next);
        setCropping(true);
      }

      setImageSize({ width: destCanvas.width, height: destCanvas.height });
      sourceReadyRef.current = true;
      fitPreview();
      getFileSize();
      setReady(true);
    };
  };

  // Reset the transformations to the initial image
  const noteEdit = (): void => {
    editVersionRef.current += 1;
    setEditVersion(editVersionRef.current);
    setSaveError(null);
  };

  const resetImage = () => {
    setRotation(0);
    setScale(1);
    setZoom(1);
    setFlipHorizontal(false);
    setFlipVertical(false);
    loadImage();
  };

  const getFileSize = async (): Promise<void> => {
    if (destCanvasRef.current) {
      const destCanvas = destCanvasRef.current;
      if (destCanvas) {
        try {
          destCanvas.toBlob(async (blob) => {
            if (blob) {
              setFileSize(formatFileSize(blob.size));
            }
          }, mimeType);
        } catch {
          // A storage address can be drawn, but the browser will not let the canvas read those pixels.
        }
      }
    }
  };


  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      // Less than 1 MB, show as KB
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
      // Less than 1 GB, show as MB
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    } else {
      // 1 GB or more, show as GB
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
  }

  // Function to apply transformations to destCanvas for immediate preview
  const updatePreview = async (): Promise<void> => {
    const srcCanvas = srcCanvasRef.current;
    const destCanvas = destCanvasRef.current;

    if (srcCanvas && destCanvas && sourceReadyRef.current && !croppingRef.current) {
      const destCtx = destCanvas.getContext('2d');

      // Calculate dimensions for rotated canvas
      const newWidth = srcCanvas.width * scale;
      const newHeight = srcCanvas.height * scale;

      // Adjust canvas size based on rotation
      if (rotation % 180 === 0) {
        destCanvas.width = newWidth;
        destCanvas.height = newHeight;
      } else {
        destCanvas.width = newHeight;
        destCanvas.height = newWidth;
      }

      // Update image size display
      setImageSize({ width: destCanvas.width, height: destCanvas.height });

      // Clear the destination canvas and apply transformations
      destCtx?.clearRect(0, 0, destCanvas.width, destCanvas.height);
      destCtx?.resetTransform();

      // Center the context for rotation and scaling
      destCtx?.translate(destCanvas.width / 2, destCanvas.height / 2);
      destCtx?.rotate((rotation * Math.PI) / 180);
      destCtx?.scale(scale * zoom, scale * zoom);
      

      // Apply horizontal and vertical flip transformations
      destCtx?.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);

      destCtx?.translate(-srcCanvas.width / 2, -srcCanvas.height / 2);

      // Draw the image with transformations on the destination canvas
      destCtx?.drawImage(srcCanvas, 0, 0);
      fitPreview();

      // get file size
      getFileSize();
    }
  };

  const handleRotate = (): void => {
    noteEdit();
    setRotation((prevRotation) => (prevRotation + 90) % 360);
  };

  const handleScaleIncrease = (): void => {
    noteEdit();
    setScale((prevScale) => prevScale + 0.1);
  };

  const handleScaleDecrease = (): void => {
    noteEdit();
    setScale((prevScale) => Math.max(0.1, prevScale - 0.1)); // Ensure scale doesn’t go below 0.1
  };

  const handleFlipHorizontal = (): void => {
    noteEdit();
    setFlipHorizontal((prevFlip) => !prevFlip);
  };

  const handleFlipVertical = (): void => {
    noteEdit();
    setFlipVertical((prevFlip) => !prevFlip);
  };

  const handleFullScreenToggle = (): void => {
    if (onFullScreenToggle) {
      onFullScreenToggle(!fullScreen);
      setFullScreen(!fullScreen);
    }
  };

  const handleZoomIn = () => {
    noteEdit();
    setZoom((prevZoom) => prevZoom + 0.1);
  };

  const handleZoomOut = () => {
    noteEdit();
    setZoom((prevZoom) => Math.max(0.1, prevZoom - 0.1)); // Prevent zoom from going below 0.1
  };

  const bitmapPoint = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvas = destCanvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    const styles = window.getComputedStyle(canvas);
    const borderLeft = parseFloat(styles.borderLeftWidth) || 0;
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderRight = parseFloat(styles.borderRightWidth) || 0;
    const borderBottom = parseFloat(styles.borderBottomWidth) || 0;
    const contentWidth = rect.width - borderLeft - borderRight;
    const contentHeight = rect.height - borderTop - borderBottom;
    if (contentWidth <= 0 || contentHeight <= 0) {
      return null;
    }

    return {
      x: ((clientX - rect.left - borderLeft) / contentWidth) * canvas.width,
      y: ((clientY - rect.top - borderTop) / contentHeight) * canvas.height
    };
  };

  const handleSizeInBitmap = (): number => {
    const canvas = destCanvasRef.current;
    if (!canvas) {
      return minCropSize;
    }

    const rect = canvas.getBoundingClientRect();
    const contentWidth = Math.max(rect.width - 2, 1);
    return (12 / contentWidth) * canvas.width;
  };

  const hitTestCrop = (point: { x: number; y: number }, rect: ICropRect): CropHandle | "new" => {
    const reach = handleSizeInBitmap();
    const nearLeft = Math.abs(point.x - rect.x) <= reach;
    const nearRight = Math.abs(point.x - (rect.x + rect.width)) <= reach;
    const nearTop = Math.abs(point.y - rect.y) <= reach;
    const nearBottom = Math.abs(point.y - (rect.y + rect.height)) <= reach;
    const insideX = point.x >= rect.x - reach && point.x <= rect.x + rect.width + reach;
    const insideY = point.y >= rect.y - reach && point.y <= rect.y + rect.height + reach;

    if (nearTop && nearLeft && insideX && insideY) return "nw";
    if (nearTop && nearRight && insideX && insideY) return "ne";
    if (nearBottom && nearLeft && insideX && insideY) return "sw";
    if (nearBottom && nearRight && insideX && insideY) return "se";
    if (nearTop && insideX && point.y >= rect.y - reach && point.y <= rect.y + rect.height) return "n";
    if (nearBottom && insideX) return "s";
    if (nearLeft && insideY) return "w";
    if (nearRight && insideY) return "e";
    if (point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height) {
      return "move";
    }

    return "new";
  };

  const beginCrop = (): void => {
    const canvas = destCanvasRef.current;
    if (!canvas || !sourceReadyRef.current || canvas.width === 0 || canvas.height === 0) {
      return;
    }

    cropCommitLock.current = false;
    const marginX = Math.round(canvas.width * 0.12);
    const marginY = Math.round(canvas.height * 0.12);
    const next = clampCrop({
      x: marginX,
      y: marginY,
      width: canvas.width - marginX * 2,
      height: canvas.height - marginY * 2
    }, canvas.width, canvas.height);
    cropRectRef.current = next;
    croppingRef.current = true;
    setCropRect(next);
    setCropping(true);
    setCropCursor("move");
  };

  const commitCrop = (): boolean => {
    const destCanvas = destCanvasRef.current;
    const srcCanvas = srcCanvasRef.current;
    const rect = cropRectRef.current;
    if (cropCommitLock.current || !croppingRef.current || !rect || !destCanvas || !srcCanvas) {
      return false;
    }

    const clamped = clampCrop(rect, destCanvas.width, destCanvas.height);
    const sw = Math.max(1, Math.round(clamped.width));
    const sh = Math.max(1, Math.round(clamped.height));
    const sx = Math.max(0, Math.min(Math.round(clamped.x), Math.max(0, destCanvas.width - sw)));
    const sy = Math.max(0, Math.min(Math.round(clamped.y), Math.max(0, destCanvas.height - sh)));
    const cropped = document.createElement("canvas");
    cropped.width = sw;
    cropped.height = sh;
    const croppedContext = cropped.getContext("2d");
    if (!croppedContext) {
      return false;
    }

    try {
      croppedContext.drawImage(destCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
      srcCanvas.width = sw;
      srcCanvas.height = sh;
      srcCanvas.getContext("2d")?.drawImage(cropped, 0, 0);
      destCanvas.width = sw;
      destCanvas.height = sh;
      destCanvas.getContext("2d")?.drawImage(cropped, 0, 0);
    } catch {
      return false;
    }

    cropCommitLock.current = true;
    croppingRef.current = false;
    cropRectRef.current = null;
    pixelEditsRef.current = true;
    destCanvas.style.width = "";
    destCanvas.style.height = "";
    setImageSize({ width: sw, height: sh });
    setPixelEdits(true);
    setCropping(false);
    setCropRect(null);
    setRotation(0);
    setScale(1);
    setFlipHorizontal(false);
    setFlipVertical(false);
    setZoom(1);
    fitPreview();
    getFileSize();
    noteEdit();
    return true;
  };

  const handleCrop = (): void => {
    if (croppingRef.current) {
      commitCrop();
      return;
    }

    beginCrop();
  };

  const handleCropPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const current = cropRectRef.current;
    const canvas = destCanvasRef.current;
    const point = bitmapPoint(event.clientX, event.clientY);
    if (!current || !canvas || !point) {
      return;
    }

    event.preventDefault();
    cropDragEndRef.current?.();
    const hit = hitTestCrop(point, current);
    const drawing = hit === "new";
    const handle: CropHandle = drawing ? "se" : hit;
    const origin = drawing
      ? { x: point.x, y: point.y, width: 0, height: 0 }
      : current;
    const drag = {
      handle,
      startX: point.x,
      startY: point.y,
      origin
    };

    if (drawing) {
      const started = clampCrop({ x: point.x, y: point.y, width: minCropSize, height: minCropSize }, canvas.width, canvas.height);
      cropRectRef.current = started;
      setCropRect(started);
    }

    cropDragRef.current = true;
    setCropCursor(cursorForHandle(drawing ? "new" : handle));
    const overlay = event.currentTarget;
    try {
      overlay.setPointerCapture?.(event.pointerId);
    } catch {
      // The window listeners still follow the pointer when capture is unavailable.
    }

    const move = (moveEvent: PointerEvent): void => {
      if (!croppingRef.current) {
        return;
      }

      const next = bitmapPoint(moveEvent.clientX, moveEvent.clientY);
      const target = destCanvasRef.current;
      if (!next || !target) {
        return;
      }

      let x = drag.origin.x;
      let y = drag.origin.y;
      let width = drag.origin.width;
      let height = drag.origin.height;
      const dx = next.x - drag.startX;
      const dy = next.y - drag.startY;

      if (drag.handle === "move") {
        x += dx;
        y += dy;
      } else {
        if (drag.handle.includes("e")) width += dx;
        if (drag.handle.includes("s")) height += dy;
        if (drag.handle.includes("w")) {
          x += dx;
          width -= dx;
        }
        if (drag.handle.includes("n")) {
          y += dy;
          height -= dy;
        }
        if (width < 0) {
          x += width;
          width = Math.abs(width);
        }
        if (height < 0) {
          y += height;
          height = Math.abs(height);
        }
      }

      const nextRect = clampCrop({ x, y, width, height }, target.width, target.height);
      cropRectRef.current = nextRect;
      setCropRect(nextRect);
    };

    const end = (): void => {
      cropDragRef.current = false;
      if (cropDragEndRef.current === end) {
        cropDragEndRef.current = null;
      }
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };

    cropDragEndRef.current = end;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  const handleCropPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (cropDragRef.current) {
      return;
    }

    const rect = cropRectRef.current;
    const point = bitmapPoint(event.clientX, event.clientY);
    if (!rect || !point) {
      return;
    }

    setCropCursor(cursorForHandle(hitTestCrop(point, rect)));
  };

  commitCropRef.current = commitCrop;

  // Effect to update the preview whenever scale, rotation, flipHorizontal, or flipVertical changes
  useEffect(() => {
    updatePreview();
  }, [scale, rotation, flipHorizontal, flipVertical, zoom]);

  const measureCanvasBox = (): void => {
    const canvas = destCanvasRef.current;
    const frameNode = previewFrameRef.current;
    if (!canvas || !frameNode) {
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const frameRect = frameNode.getBoundingClientRect();
    const styles = window.getComputedStyle(canvas);
    const frameStyles = window.getComputedStyle(frameNode);
    const borderLeft = parseFloat(styles.borderLeftWidth) || 0;
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderRight = parseFloat(styles.borderRightWidth) || 0;
    const borderBottom = parseFloat(styles.borderBottomWidth) || 0;
    const frameBorderLeft = parseFloat(frameStyles.borderLeftWidth) || 0;
    const frameBorderTop = parseFloat(frameStyles.borderTopWidth) || 0;
    setCanvasBox({
      left: canvasRect.left - frameRect.left - frameBorderLeft + borderLeft,
      top: canvasRect.top - frameRect.top - frameBorderTop + borderTop,
      width: Math.max(0, canvasRect.width - borderLeft - borderRight),
      height: Math.max(0, canvasRect.height - borderTop - borderBottom)
    });
  };

  const bakePreview = (): boolean => {
    const destCanvas = destCanvasRef.current;
    const srcCanvas = srcCanvasRef.current;
    if (!destCanvas || !srcCanvas || destCanvas.width === 0 || destCanvas.height === 0) {
      return false;
    }

    const baked = document.createElement("canvas");
    baked.width = destCanvas.width;
    baked.height = destCanvas.height;
    const bakedContext = baked.getContext("2d");
    if (!bakedContext) {
      return false;
    }

    try {
      bakedContext.drawImage(destCanvas, 0, 0);
      srcCanvas.width = baked.width;
      srcCanvas.height = baked.height;
      srcCanvas.getContext("2d")?.drawImage(baked, 0, 0);
      destCanvas.getContext("2d")?.drawImage(baked, 0, 0);
    } catch {
      return false;
    }

    pixelEditsRef.current = true;
    croppingRef.current = false;
    cropRectRef.current = null;
    setPixelEdits(true);
    setCropping(false);
    setCropRect(null);
    setRotation(0);
    setScale(1);
    setZoom(1);
    setFlipHorizontal(false);
    setFlipVertical(false);
    setImageSize({ width: destCanvas.width, height: destCanvas.height });
    fitPreview();
    getFileSize();
    return true;
  };

  const exportCurrent = (receive: (blob: Blob) => void): void => {
    const destCanvas = destCanvasRef.current;
    if (!destCanvas) {
      return;
    }

    try {
      destCanvas.toBlob((blob) => {
        if (blob) {
          receive(blob);
        }
      }, mimeType);
    } catch {
      // The storage address can be shown, but the browser will not export those pixels.
    }
  };

  const hasPendingEdit = cropping || editVersion !== appliedVersion;
  const saveReady = Boolean(onSave) && !hasPendingEdit && editVersion > 0;

  // Apply keeps the edited picture on screen. Save is offered only after that preview settles.
  const applyChanges = (): void => {
    if (saving || !ready) {
      return;
    }

    if (saveReady) {
      void saveChanges();
      return;
    }

    if (onSave && !hasPendingEdit) {
      return;
    }

    if (croppingRef.current && !commitCrop()) {
      return;
    }

    if (onSave) {
      if (!bakePreview()) {
        return;
      }

      setAppliedVersion(editVersionRef.current);
      return;
    }

    exportCurrent(onApplyChanges);
  };

  const saveChanges = async (): Promise<void> => {
    const destCanvas = destCanvasRef.current;
    if (!destCanvas || !onSave) {
      return;
    }

    setSaving(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) => {
        try {
          destCanvas.toBlob((next) => resolve(next), mimeType);
        } catch {
          resolve(null);
        }
      });

      if (!blob) {
        setSaveError("The preview could not be saved yet. Wait for the image to finish loading, then try again.");
        return;
      }
      setSaveError(null);

      const saved = await onSave(blob);
      if (saved) {
        editVersionRef.current = 0;
        setEditVersion(0);
        setAppliedVersion(0);
      }
    } catch {
      setSaveError("The image could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const snapshot = editSnapshotRef.current;
    if (snapshot.pixelEdits || snapshot.transformed) {
      return;
    }

    loadImage(croppingRef.current);
  }, [mediaItem.Url]);

  useEffect(() => {
    if (ready) {
      updatePreview();
    }
  }, [ready]);

  useEffect(() => {
    if (!cropping) {
      return;
    }

    fitPreview();
    const frameId = requestAnimationFrame(measureCanvasBox);
    return () => cancelAnimationFrame(frameId);
  }, [cropping, imageSize.width, imageSize.height]);

  useEffect(() => {
    if (!cropping) {
      return;
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        croppingRef.current = false;
        cropRectRef.current = null;
        cropDragRef.current = false;
        setCropping(false);
        setCropRect(null);
      } else if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        commitCropRef.current();
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [cropping]);

  useEffect(() => {
    const frame = previewFrameRef.current;
    if (!frame) {
      return;
    }

    const observer = new ResizeObserver(() => {
      fitPreview();
      if (croppingRef.current) {
        measureCanvasBox();
      }
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="image-editor w-100 h-100 d-flex flex-column overflow-hidden">
      <div className="toolbar d-flex justify-content-between align-items-center bg-secondary mb-2 p-2 flex-wrap gap-2">
        <div className="d-flex flex-wrap gap-1">
          <Button variant="secondary" className="btn-nomin" onClick={handleZoomIn} title="zoom in" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleZoomOut} title="zoom out" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleScaleIncrease} title="increase size" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleScaleDecrease} title="decrease size" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faMinus} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleRotate} title="rotate 90º" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleFlipHorizontal} title="flip horizontally" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faArrowsAltH} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={handleFlipVertical} title="flip vertically" disabled={!ready || cropping || saving}>
            <FontAwesomeIcon icon={faArrowsAltV} />
          </Button>
          <Button variant={cropping ? "warning" : "secondary"} className="btn-nomin" onClick={handleCrop} title={cropping ? "Apply crop" : "Crop"} disabled={!ready || saving} aria-pressed={cropping}>
            <FontAwesomeIcon icon={faCrop} />
          </Button>
          <Button variant="secondary" className="btn-nomin" onClick={resetImage} title="reset to original" disabled={saving}>
            <FontAwesomeIcon icon={faUndo} />
          </Button>
          <Button variant="secondary" className="btn-nomin" title="full screen" onClick={handleFullScreenToggle}>
            <FontAwesomeIcon icon={fullScreen ? normalviewicon : fullscreenicon} />
          </Button>
          <Button
            variant="success"
            onClick={applyChanges}
            title={saveReady ? "Save image" : "Apply changes"}
            disabled={!ready || saving || (Boolean(onSave) && !saveReady && !hasPendingEdit)}
          >
            <FontAwesomeIcon icon={faCheck} />&nbsp;{saving ? "Saving..." : saveReady ? "Save" : "Apply"}
          </Button>
        </div>
        <div className="text-light">
          {cropping && cropRect
            ? <>Crop: {Math.round(cropRect.width)} x {Math.round(cropRect.height)}<br />Click crop again to keep this area</>
            : <>Size: {imageSize.width} x {imageSize.height}<br />File Size: {fileSize}{saveReady && saveHint ? <><br />{saveHint}</> : null}{saveError ? <><br />{saveError}</> : null}</>}
        </div>
      </div>

      <div
        ref={previewFrameRef}
        className="d-flex justify-content-center align-items-center m-0 p-2 border flex-grow-1 overflow-hidden position-relative"
        style={{ minHeight: 0, minWidth: 0 }}
      >
        <canvas ref={srcCanvasRef} style={{ display: "none" }} />
        <img
          src={mediaItem.Url}
          alt=""
          style={{
            position: "absolute",
            inset: "0.5rem",
            width: "calc(100% - 1rem)",
            height: "calc(100% - 1rem)",
            objectFit: "contain",
            display: showCanvas ? "none" : "block"
          }}
        />
        <canvas
          ref={destCanvasRef}
          style={{
            borderStyle: "dashed",
            borderWidth: "1px",
            borderColor: "lightgrey",
            display: showCanvas ? "block" : "none",
            boxSizing: "border-box"
          }}
        />
        {ready && cropping && cropRect && canvasBox && destCanvasRef.current && (
          <div
            data-crop-overlay="true"
            onPointerDown={handleCropPointerDown}
            onPointerMove={handleCropPointerMove}
            style={{
              position: "absolute",
              left: canvasBox.left,
              top: canvasBox.top,
              width: canvasBox.width,
              height: canvasBox.height,
              touchAction: "none",
              userSelect: "none",
              cursor: cropCursor,
              zIndex: 2
            }}
          >
            <div
              style={{
                position: "absolute",
                boxSizing: "border-box",
                left: `${(cropRect.x / destCanvasRef.current.width) * 100}%`,
                top: `${(cropRect.y / destCanvasRef.current.height) * 100}%`,
                width: `${(cropRect.width / destCanvasRef.current.width) * 100}%`,
                height: `${(cropRect.height / destCanvasRef.current.height) * 100}%`,
                border: "2px solid #fff",
                boxShadow: "0 0 0 9999px rgba(22, 50, 79, 0.45)",
                backgroundImage: "linear-gradient(to right, transparent 33%, rgba(255,255,255,0.75) 33%, rgba(255,255,255,0.75) calc(33% + 1px), transparent calc(33% + 1px), transparent 66%, rgba(255,255,255,0.75) 66%, rgba(255,255,255,0.75) calc(66% + 1px), transparent calc(66% + 1px)), linear-gradient(to bottom, transparent 33%, rgba(255,255,255,0.75) 33%, rgba(255,255,255,0.75) calc(33% + 1px), transparent calc(33% + 1px), transparent 66%, rgba(255,255,255,0.75) 66%, rgba(255,255,255,0.75) calc(66% + 1px), transparent calc(66% + 1px))"
              }}
            >
              {cropHandleNames.map((handle) => (
                <span key={handle} style={cropHandleStyle(handle)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageEditor;
